import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { memo, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { useFormState } from 'react-hook-form';
import { useResizeListener } from '@Hooks/custom/useResizeListener.ts';
import { getErrorFieldNames, useScrollToFirstError } from '@Hooks/index';
import { SUBMIT_BUTTON_ID } from '@Types/consts.ts';
import { useNavigationManager } from '@Router/useNavigationManager';
import { useRouteTracker } from '@Providers/RouteTrackerProvider/context.ts';
import {
  reportOnboardingStepContinueClicked,
  reportOnboardingValidationError,
} from '@Services/analytics/onboardingValidationAnalytics';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type React from 'react';
interface IFormFooterProps {
  isLoading: boolean;
  onSubmit: () => void;
  maxWidth?: number;
  formMethods: UseFormReturn<FieldValues>;
  customBackHandling?: () => void;
  customSubmitTitle?: string;
  footerInputSlot?: React.ReactNode;
  shouldStrechCta?: boolean;
  /** @deprecated No longer disables the CTA — validation now runs on click. */
  isFormValid?: boolean;
  skipFormValidation?: boolean;
  /** Keep the CTA disabled until the form is valid (legacy behavior, used by Review). */
  disableWhenInvalid?: boolean;
  /** Skip focusing the first invalid field on failed validation (scroll still happens). */
  focusFirstError?: boolean;
  centerCta?: boolean;
  showBackButton?: boolean;
  showNextButton?: boolean;
}

export const FormFooter = memo(
  ({
    isLoading,
    onSubmit,
    maxWidth,
    formMethods,
    customBackHandling,
    customSubmitTitle,
    footerInputSlot,
    shouldStrechCta,
    skipFormValidation,
    isFormValid,
    disableWhenInvalid = false,
    focusFirstError = true,
    centerCta,
    showBackButton = true,
    showNextButton = true,
  }: IFormFooterProps) => {
    const { t } = useTranslation();
    const { handleSubmit } = formMethods;
    const { isValid, errors: formErrors } = useFormState({ control: formMethods.control });
    const scrollToFirstError = useScrollToFirstError({ focus: focusFirstError });
    const { currentRoute } = useRouteTracker();
    const attemptRef = useRef({ step: '', count: 0 });

    const getAttemptNumber = () => {
      if (attemptRef.current.step !== currentRoute) {
        attemptRef.current = { step: currentRoute, count: 0 };
      }
      attemptRef.current.count += 1;
      return attemptRef.current.count;
    };

    const { navigateToPreviousPage, isAllowedPrevPage } = useNavigationManager();
    const { isMobile } = useResizeListener();

    const getDynamicMobileLayout = () => {
      if (footerInputSlot) {
        return 'w-full flex flex-col new-tab-landscape:flex-row items-center justify-between gap-[20px] mx-auto';
      }
      if (centerCta) {
        return 'w-full flex items-center justify-center mx-auto';
      }
      return 'w-full flex items-center justify-between mx-auto';
    };
    // Default: only an in-flight request disables the CTA (validation runs on
    // click). With disableWhenInvalid, restore the legacy disabled-until-valid gate.
    const hasErrors = Object.keys(formErrors).length > 0;
    const isDisabled = disableWhenInvalid
      ? isLoading ||
        (!skipFormValidation && (!isValid || hasErrors)) ||
        (isFormValid !== undefined && !isFormValid)
      : isLoading;

    const handleContinue = () => {
      if (skipFormValidation) {
        onSubmit();
        return;
      }

      void handleSubmit(
        () => {
          reportOnboardingStepContinueClicked({
            step: currentRoute,
            isValid: true,
            invalidFields: [],
            attemptNumber: getAttemptNumber(),
          });
          onSubmit();
        },
        (errors) => {
          const invalidFields = getErrorFieldNames(errors);
          reportOnboardingStepContinueClicked({
            step: currentRoute,
            isValid: false,
            invalidFields,
            attemptNumber: getAttemptNumber(),
          });
          reportOnboardingValidationError({ step: currentRoute, fieldErrors: invalidFields });
          scrollToFirstError(errors);
        }
      )();
    };

    // Hide footer on mobile if next button is hidden
    if (isMobile && !showNextButton) {
      return null;
    }

    return (
      <div
        className='w-full p-[16px] md:p-[24px] z-40 bg-surface-primary-bw-00 sm:block shrink-0 border-t border-outline-primary-bw-03'
        id='page-footer'>
        <div
          style={{ maxWidth: maxWidth ? `${maxWidth}px` : '500px' }}
          className={getDynamicMobileLayout()}>
          {footerInputSlot ? (
            <div className='min-w-0 w-full'>{footerInputSlot}</div>
          ) : !centerCta ? (
            <div
              className={
                !showBackButton || !isAllowedPrevPage
                  ? 'hidden sm:block sm:opacity-0 sm:pointer-events-none w-[115px]'
                  : 'hidden sm:block sm:opacity-100 sm:pointer-events-auto w-[115px]'
              }>
              <LiliButton
                onClick={() => (customBackHandling ?? navigateToPreviousPage)()}
                title='Back'
                id='back'
                data-testid='back-button'
                type={LiliButtonType.SECONDARY}
                className='text-text-main-primary'
              />
            </div>
          ) : null}

          <div
            className={
              !showNextButton
                ? 'hidden-placeholder'
                : twMerge('w-full min-w-[115px] shrink-0', !shouldStrechCta && 'sm:w-fit')
            }>
            <LiliButton
              onClick={handleContinue}
              isLoading={isLoading}
              disabled={isDisabled}
              id={SUBMIT_BUTTON_ID}
              type={LiliButtonType.PRIMARY}
              title={customSubmitTitle || t('global.FORM_SUBMIT')}
              className='new-tab-landscape:whitespace-nowrap max-h-none'
              data-testid='continue-button'
            />
          </div>
        </div>
      </div>
    );
  }
);

FormFooter.displayName = 'FormFooter';
